import { ReactElement, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FeatureType } from '@lessonPlanner/constants';

import GenericUnpackingLP from '../components/generic-unpacking/GenericUnpackingLP';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import DynamicLabelGenerateButton from '../components/molecules/DynamicLabelGenerateButton';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import { defaultCreditCost } from '../constants/credits';
import formDefaultValues from '../constants/forms/generic-unpacking/defaults';
import FORM_CONTENT_MAPPER_KEYS from '../constants/forms/generic-unpacking/formContentMapperKeys';
import FORM_KEYS from '../constants/forms/generic-unpacking/formKeys';
import {
  GENERIC_UNPACKING_DESCRIPTION,
  GENERIC_UNPACKING_TITLE,
} from '../constants/genericUnpacking';
import { useGeneratePause } from '../context/generate/provider';
import { GenericUnpackingProperties, PageKey } from '../enums/pageKey';
import SidebarLayout from '../layouts/SidebarLayout';
import { genericUnpackingProperties } from '../types/page';
import { mapFormContents } from '../utils/mapFormContents';

function getDefaultValues() {
  const defaultValues = formDefaultValues();
  return defaultValues.GenericUnpackingLP;
}

function getFormContentMapper() {
  return FORM_CONTENT_MAPPER_KEYS.GenericUnpackingLP;
}

export default function Index(): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const defaultValues = getDefaultValues();
  const formContentMapper = getFormContentMapper();

  const schema = yup.object().shape({
    [FORM_KEYS.subject]: yup.string().required('Subject is required'),
    [FORM_KEYS.gradeLevel]: yup.string().required('Grade Level is required'),
    [FORM_KEYS.itemToUnpack]: yup
      .string()
      .required('Learning Competency is required'),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    trigger: triggerValidation,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const watchedValues = watch();

  const banner = (
    <GenericBanner
      showTutorial
      content={GENERIC_UNPACKING_DESCRIPTION}
      title={GENERIC_UNPACKING_TITLE}
    />
  );

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();

  const hasErrors = Object.entries(errors).length > 0;

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.genericUnpacking];

  const formContents = {
    ...mapFormContents(defaultValues, watchedValues, formContentMapper),
    [GenericUnpackingProperties.feature]: FeatureType.GENERIC_UNPACKING,
    [GenericUnpackingProperties.unpackType]: 'Learning Competency',
  };

  const customFormValidator = async () => {
    await triggerValidation();
  };

  return (
    <MainContent
      banner={banner}
      formContents={formContents as genericUnpackingProperties}
      pageKey={PageKey.genericUnpacking}
      setIsLoading={setIsLoading}
    >
      <>
        <GenericUnpackingLP
          errors={errors}
          setValue={setValue}
          triggerValidation={triggerValidation}
          watchedValues={watchedValues}
        />

        <div className="col-span-full">
          <div className="flex flex-col">
            <DynamicLabelGenerateButton
              isRegularDefault
              buttonText={buttonText}
              creditCost={defaultCreditCost}
              handleOnCLick={customFormValidator}
              isDisabled={isGenerateButtonDisabled}
            />
          </div>
        </div>
      </>
    </MainContent>
  );
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
